import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { OnboardingPreferencesStep } from './OnboardingPreferencesStep'
import { OnboardingTemplateStep } from './OnboardingTemplateStep'
import { OnboardingPlanStep } from './OnboardingPlanStep'
import { OnboardingGenerateStep } from './OnboardingGenerateStep'
import { useOnboardingInstrumentation } from './useOnboardingInstrumentation'
import type { OnboardingStep, OnboardingPreferences } from './types'

const STEPS: OnboardingStep[] = ['preferences', 'templates', 'plan', 'generate']
const STEP_COUNT = STEPS.length

function StepIndicator({ currentStep }: { currentStep: OnboardingStep }) {
  const { t } = useTranslation('onboarding')
  const currentIndex = STEPS.indexOf(currentStep)
  return (
    <div className="flex items-center justify-center gap-2 mb-6" aria-label={`Step ${currentIndex + 1} of ${STEP_COUNT}`}>
      {STEPS.map((step, i) => (
        <div key={step} className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 ${i === currentIndex ? 'text-accent' : 'text-muted'}`}>
            <div className={`h-2 w-2 rounded-full ${i === currentIndex ? 'bg-accent' : i < currentIndex ? 'bg-accent/40' : 'bg-line'}`} />
            <span className="text-xs font-medium">{t(`step.${step}`)}</span>
          </div>
          {i < STEP_COUNT - 1 && <div className="h-px w-6 bg-line" />}
        </div>
      ))}
    </div>
  )
}

export function OnboardingWizard() {
  const [step, setStep] = useState<OnboardingStep>('preferences')
  const [preferences, setPreferences] = useState<OnboardingPreferences | null>(null)
  const [mealsAddedCount, setMealsAddedCount] = useState(() => {
    const stored = sessionStorage.getItem('onboarding_meals_added')
    return stored ? parseInt(stored, 10) || 0 : 0
  })
  const [slotsFilled, setSlotsFilled] = useState(0)
  const queryClient = useQueryClient()
  const { recordCompletion, recordStepEntered, recordResumed } = useOnboardingInstrumentation()

  // On mount: detect resumed session (recipes in cache = templates were added previously)
  useEffect(() => {
    const existingRecipes = queryClient.getQueryData(['recipes', 'list'])
    if (existingRecipes) {
      recordResumed('preferences')
    } else {
      recordStepEntered('preferences')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMealAdded = () => {
    setMealsAddedCount((c) => {
      const next = c + 1
      sessionStorage.setItem('onboarding_meals_added', String(next))
      return next
    })
  }

  const handleStepChange = (newStep: OnboardingStep) => {
    setStep(newStep)
    recordStepEntered(newStep)
  }

  const handlePreferencesContinue = (prefs: OnboardingPreferences) => {
    setPreferences(prefs)
    handleStepChange('templates')
  }

  const handlePreferencesSkip = () => {
    setPreferences(null)
    handleStepChange('templates')
  }

  return (
    <main className="min-h-screen flex flex-col bg-canvas p-4">
      <div className="w-full max-w-2xl mx-auto flex flex-col flex-1">
        <div className="text-center mb-4">
          <span className="text-3xl font-bold tracking-tight text-accent">mise</span>
        </div>
        <StepIndicator currentStep={step} />
        {step === 'preferences' && (
          <OnboardingPreferencesStep
            onContinue={handlePreferencesContinue}
            onSkip={handlePreferencesSkip}
          />
        )}
        {step === 'templates' && (
          <OnboardingTemplateStep
            preferences={preferences}
            onMealAdded={handleMealAdded}
            mealsAddedCount={mealsAddedCount}
            onContinue={() => handleStepChange('plan')}
          />
        )}
        {step === 'plan' && (
          <OnboardingPlanStep
            householdSize={preferences?.householdSize ?? null}
            onGenerate={(filled) => {
              setSlotsFilled(filled)
              handleStepChange('generate')
            }}
          />
        )}
        {step === 'generate' && (
          <OnboardingGenerateStep
            mealsAddedCount={mealsAddedCount}
            slotsFilled={slotsFilled}
            onRecordCompletion={recordCompletion}
          />
        )}
      </div>
    </main>
  )
}
